import { useEffect, useRef, useCallback } from 'react';

export function usePagedScroll(pageCount) {
    const isTouchDevice =
        typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    const containerRef = useRef(null);

    const wheelLockRef = useRef(false);
    const wheelAccumRef = useRef(0);
    const wheelResetTimerRef = useRef(null);
    const activeIndexRef = useRef(0);
    const touchLockRef = useRef(false);

    const WHEEL_THRESHOLD = 60;
    const WHEEL_LOCK_MS = 420;
    const WHEEL_RESET_MS = 120;

    /* =========
       Scroll programático por índice
    ========= */
    const scrollToIndex = useCallback((idx) => {
        const el = containerRef.current;
        if (!el) return;

        const pageH = el.clientHeight || 1;
        const next = Math.max(0, Math.min(idx, pageCount - 1));

        activeIndexRef.current = next;

        el.scrollTo({
            top: next * pageH,
            behavior: 'smooth',
        });
    }, [pageCount]);

    const goTo = useCallback((idx) => {
        scrollToIndex(idx);
    }, [scrollToIndex]);

    /* =========
       Sincroniza índice en scroll manual
    ========= */
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onScroll = () => {
            if (isTouchDevice) {
                if (touchLockRef.current) return;

                touchLockRef.current = true;
                setTimeout(() => {
                    touchLockRef.current = false;
                }, 120);
            }

            const pageH = el.clientHeight || 1;
            const rawIndex = el.scrollTop / pageH;

            activeIndexRef.current = isTouchDevice
                ? Math.floor(rawIndex + 0.35)
                : Math.round(rawIndex);
        };

        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [isTouchDevice]);

    /* =========
       Wheel hijack (solo desktop)
    ========= */
    useEffect(() => {
        if (isTouchDevice) return;

        const el = containerRef.current;
        if (!el) return;

        const getCurrentIndex = () => {
            const pageH = el.clientHeight || 1;
            return Math.round(el.scrollTop / pageH);
        };

        const onWheel = (e) => {
            if (e.ctrlKey) return;

            e.preventDefault();
            if (wheelLockRef.current) return;

            wheelAccumRef.current += e.deltaY;

            if (wheelResetTimerRef.current) {
                clearTimeout(wheelResetTimerRef.current);
            }

            wheelResetTimerRef.current = setTimeout(() => {
                wheelAccumRef.current = 0;
            }, WHEEL_RESET_MS);

            if (Math.abs(wheelAccumRef.current) < WHEEL_THRESHOLD) return;

            const dir = wheelAccumRef.current > 0 ? 1 : -1;
            wheelAccumRef.current = 0;

            const current = getCurrentIndex();
            const next = Math.max(
                0,
                Math.min(current + dir, pageCount - 1)
            );

            if (next === current) return;

            wheelLockRef.current = true;
            scrollToIndex(next);

            window.setTimeout(() => {
                wheelLockRef.current = false;
            }, WHEEL_LOCK_MS);
        };

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', onWheel);
            if (wheelResetTimerRef.current) {
                clearTimeout(wheelResetTimerRef.current);
            }
        };
    }, [pageCount, isTouchDevice, scrollToIndex]);

    /* =========
   Desactivar scrollSnap durante touch (mobile premium UX)
========= */
    useEffect(() => {
        if (!isTouchDevice) return;

        const el = containerRef.current;
        if (!el) return;

        const disableSnap = () => {
            el.style.scrollSnapType = 'none';
        };

        const enableSnap = () => {
            el.style.scrollSnapType = 'y mandatory';
        };

        el.addEventListener('touchstart', disableSnap, { passive: true });
        el.addEventListener('touchend', enableSnap);
        el.addEventListener('touchcancel', enableSnap);

        return () => {
            el.removeEventListener('touchstart', disableSnap);
            el.removeEventListener('touchend', enableSnap);
            el.removeEventListener('touchcancel', enableSnap);
        };
    }, [isTouchDevice]);

    return {
        containerRef,
        goTo,
    };
}
